import { axiosInstance } from '@/lib/axios';

export type TruckStatus = 'active' | 'maintenance' | 'out_of_service';
export const TRUCK_STATUSES: TruckStatus[] = ['active', 'maintenance', 'out_of_service'];

export type FleetErrorCode = 'gps_not_connected' | 'gps_unavailable' | 'gps_auth' | 'not_found' | 'validation' | 'conflict';
export type DeviceStatus = 'online' | 'offline' | 'unknown';

export interface GpsPosition {
  /** Traccar's position id; absent from an older GPS */
  id?: number;
  latitude: number;
  longitude: number;
  speedKmh: number;
  course: number | null;
  fixTime: string;
  valid?: boolean;
  ignition: boolean | null;
  /** GeoPoint's vehicle state: driving, manoeuvre, idle, contact, parked, pending or resuming */
  vehicleState?: string | null;
  engineOn?: boolean | null;
  parkedSince?: string | null;
  totalDistanceM?: number | null;
}

export interface Truck {
  id: string;
  plate: string;
  model: string | null;
  status: TruckStatus;
  traccarDeviceId: number | null;
  syncError: FleetErrorCode | null;
  syncedAt: string | null;
  defaultDriverId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Only in the list: null when unlinked or GPS does not have the tracker */
  gps?: { status: DeviceStatus; lastUpdate: string | null; position: GpsPosition | null } | null;
}

/** GeoPoint's live stream: live while it is open and Traccar is up */
export type FeedState = 'live' | 'connecting' | 'down';

export interface TrucksView {
  data: Truck[];
  gpsError: FleetErrorCode | null;
  /** null when the live stream is not set up */
  feed: { state: FeedState; since: string; lastMessageAt: string | null } | null;
}

export type SyncResult = { ok: true } | { ok: false; error: FleetErrorCode } | null;

export interface TruckSaved {
  data: Truck;
  sync: SyncResult;
}

export interface GpsDevice {
  id: number;
  name: string;
  uniqueId: string;
  status: DeviceStatus;
  lastUpdate: string | null;
  position: GpsPosition | null;
  linkedTruckId: string | null;
}

export interface ImportResult {
  created: number;
  skipped: Array<{ deviceId: number; name: string; reason: 'plate_taken' | 'invalid_name' | 'already_linked' }>;
}

export interface TruckInput {
  plate: string;
  /** Only when editing: echo ignores it on create */
  defaultDriverId?: string | null;
  model: string | null;
  status: TruckStatus;
}

const BASE = '/api/fleet';

export const getTrucks = async (): Promise<TrucksView> => (await axiosInstance.get<TrucksView>(`${BASE}/trucks`)).data;
export const createTruck = async (input: TruckInput): Promise<Truck> =>
  (await axiosInstance.post<{ data: Truck }>(`${BASE}/trucks`, input)).data.data;
export const updateTruck = async (id: string, input: Partial<TruckInput>): Promise<TruckSaved> =>
  (await axiosInstance.put<TruckSaved>(`${BASE}/trucks/${id}`, input)).data;
export const deleteTruck = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${BASE}/trucks/${id}`);
};
export const syncTruck = async (id: string): Promise<TruckSaved> =>
  (await axiosInstance.post<TruckSaved>(`${BASE}/trucks/${id}/sync`)).data;
export const getGpsDevices = async (): Promise<GpsDevice[]> =>
  (await axiosInstance.get<{ data: GpsDevice[] }>(`${BASE}/gps/devices`)).data.data;
export const linkTruck = async (id: string, deviceId: number): Promise<TruckSaved> =>
  (await axiosInstance.put<TruckSaved>(`${BASE}/trucks/${id}/link`, { deviceId })).data;
export const unlinkTruck = async (id: string): Promise<Truck> =>
  (await axiosInstance.delete<{ data: Truck }>(`${BASE}/trucks/${id}/link`)).data.data;
export const importTrucks = async (): Promise<ImportResult> =>
  (await axiosInstance.post<{ data: ImportResult }>(`${BASE}/import`)).data.data;

export type LoadStatus = 'planned' | 'in_transit' | 'delivered' | 'cancelled';
export const LOAD_STATUSES: LoadStatus[] = ['planned', 'in_transit', 'delivered', 'cancelled'];

export interface Customer {
  id: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  active: boolean;
  /** Only in the list: Delete is offered only without loads */
  hasLoads?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerInput {
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  active: boolean;
}

/** A driver as echo's driverViews answers it: profile + account + default truck */
export interface Driver {
  id: string;
  userId: string;
  name: string;
  username: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  active: boolean;
  /** Invited and has not set a password yet */
  invited: boolean;
  /** false once the account lost the DRIVER role (echo then also reports active and invited false) */
  isDriver: boolean;
  defaultTruck: { id: string; plate: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface NewDriverInput {
  name: string;
  phone: string | null;
  username: string | null;
  email: string | null;
  notes: string | null;
  /** The invitation email's language: en, es or sr */
  language: string;
}

export interface DriverChangeInput {
  name?: string;
  phone?: string | null;
  notes?: string | null;
}

export interface DriverInvitation {
  data: Driver;
  invitationLink: string;
  emailSent: boolean;
}

/** A middle stop may be both: some goods off, others on */
export type StopKind = 'pickup' | 'delivery' | 'both';

/** One place on a load's way: the first is the pickup, the last the delivery */
export interface Stop {
  kind: StopKind;
  place: string;
  /** ISO UTC; always set on the first and last stop */
  at: string | null;
  /** The place OpenRouteService matched; null until routed */
  matched: string | null;
  /** Where the stop is: picked by the dispatcher (pinned), or found by echo from the place */
  lat: number | null;
  lon: number | null;
  pinned: boolean;
}

export interface StopInput {
  kind: StopKind;
  place: string;
  at: string | null;
  /** A picked point: the stop is pinned there */
  lat?: number;
  lon?: number;
}

/** A load as echo's loadViews answers it. Times are ISO UTC. */
export interface Load {
  id: string;
  number: number;
  customerId: string;
  customerName: string;
  origin: string;
  destination: string;
  /** null: no start yet ("bez termina") */
  pickupAt: string | null;
  deliveryAt: string | null;
  /** Every stop in order; origin, destination, pickupAt and deliveryAt copy the first and last */
  stops: Stop[];
  cargo: string | null;
  /** EUR */
  price: number | null;
  truckId: string | null;
  truckPlate: string | null;
  driverId: string | null;
  driverName: string | null;
  status: LoadStatus;
  late: boolean;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  notes: string | null;
  routeKey: string | null;
  distanceM: number | null;
  driveS: number | null;
  routeFrom: string | null;
  routeTo: string | null;
  /** The dispatcher's own duration, seconds; null: the route's drive time is the duration */
  estimateS: number | null;
  /** The route option the dispatcher chose */
  routePreference: RoutePreference;
  route: 'ok' | 'not_found' | 'pending' | 'off';
  /** Praćenje GPS-om: the tracker reads this tura and may set its status */
  tracking: boolean;
  /** What the tracker last read; null when it never read the tura */
  trackingState: TrackingState | null;
  /** The tura's pauses and rests on the way */
  breaks: Break[];
  createdAt: string;
  updatedAt: string;
}

/** What the truck is doing for the tura (echo's tracking/tura.ts) */
export type TrackingPhase = 'waiting' | 'to_pickup' | 'at_stop' | 'driving' | 'stopped' | 'pause' | 'rest' | 'done';
export type FlagKind = 'late' | 'gps_silent' | 'no_route' | 'not_started' | 'unclear' | 'off_route';

export interface TrackingFlag {
  kind: FlagKind;
  severity: 'red' | 'amber' | 'grey';
  since: string;
  detail: Record<string, unknown>;
}

export interface TrackingState {
  phase: TrackingPhase;
  phaseSince: string;
  /** The stop of an at_stop phase, from 1 */
  stopPosition: number | null;
  /** 0–1; null without a route line */
  progress: number | null;
  drivenM: number | null;
  remainingM: number | null;
  /** Road metres to the pickup before the start; null once it started or when it cannot be measured */
  pickupM: number | null;
  /** How the progress was measured: ORS road distance, or the planned line's projection */
  progressSource: 'ors' | 'line' | null;
  routeM: number | null;
  eta: string | null;
  /** Against the final stop's planned time, seconds; negative is early */
  etaDelayS: number | null;
  lastFixAt: string | null;
  /** The start and delivery the GPS shows */
  startedAt: string | null;
  deliveredAt: string | null;
  visits: Array<{ arrivedAt: string; departedAt: string | null; nearby?: true } | null>;
  flags: TrackingFlag[];
  /** When the tracker last read the tura */
  computedAt: string;
  /** Claude's note "what to watch", when it wrote it, and with which model */
  note?: string | null;
  noteAt?: string | null;
  noteModel?: string | null;
}

export interface Break {
  kind: 'pause' | 'rest';
  from: string;
  /** null while it lasts */
  to: string | null;
}

export type TuraEventKind = 'arrived' | 'departed' | 'pause' | 'rest' | 'status' | 'tracking' | 'decision' | 'deviation';

/** One entry of a tura's timeline */
export interface TuraEvent {
  id: string;
  at: string;
  until: string | null;
  kind: TuraEventKind;
  stopPosition: number | null;
  source: 'autopilot' | 'person' | 'ai';
  userId: string | null;
  detail: Record<string, unknown>;
}

/** A tura's tracking for its card: the switch, the state, the timeline, where the truck drove and the route line */
export interface TuraTracking {
  tracking: boolean;
  trackingState: TrackingState | null;
  events: TuraEvent[];
  /** [lat, lon] from the start to the delivery or now */
  track: Array<[number, number]>;
  routeGeometry: string | null;
  /** Where the truck left the planned route: the way it went (encoded polyline) and what it cost; endedAt null while off */
  deviations: Array<{ startedAt: string; endedAt: string | null; drivenM: number; plannedM: number | null; extraM: number | null; extraS: number | null; path: string; via: string | null }>;
  /** How often turas on this relation (first and final town) went another way */
  relation: { from: string; to: string; turas: number; deviated: number; recent: Array<{ number: number | null; startedAt: string; extraM: number | null; extraS: number | null; via: string | null }> };
}

/** A tura on Uživo, with its truck's GPS now */
export interface LiveTura extends Load {
  gps: { state: string | null; speedKmh: number; at: string; lat: number; lon: number } | null;
}

export interface LiveBoard {
  /** null when the live stream is not set up */
  feed: { state: FeedState; since: string; lastMessageAt: string | null } | null;
  turas: LiveTura[];
}

export interface LoadInput {
  customerId: string;
  stops: StopInput[];
  /** Left out: keep the saved estimate; null: use the route */
  estimateS?: number | null;
  /** Left out: keep the saved option */
  routePreference?: RoutePreference;
  cargo: string | null;
  price: number | null;
  truckId: string | null;
  driverId: string | null;
  notes: string | null;
}

/** GET /loads: from and to together (ISO), or neither; unassigned adds every load without a truck */
export interface LoadQuery {
  from?: string;
  to?: string;
  status?: LoadStatus;
  customerId?: string;
  unassigned?: boolean;
}

export interface MyLoads {
  active: Load[];
  delivered: Load[];
}

export const getCustomers = async (): Promise<Customer[]> =>
  (await axiosInstance.get<{ data: Customer[] }>(`${BASE}/customers`)).data.data;
export const createCustomer = async (input: CustomerInput): Promise<Customer> =>
  (await axiosInstance.post<{ data: Customer }>(`${BASE}/customers`, input)).data.data;
export const updateCustomer = async (id: string, input: Partial<CustomerInput>): Promise<Customer> =>
  (await axiosInstance.put<{ data: Customer }>(`${BASE}/customers/${id}`, input)).data.data;
export const deleteLoad = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${BASE}/loads/${id}`);
};

export const deleteCustomer = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${BASE}/customers/${id}`);
};

export const getDrivers = async (): Promise<Driver[]> =>
  (await axiosInstance.get<{ data: Driver[] }>(`${BASE}/drivers`)).data.data;
export const createDriver = async (input: NewDriverInput): Promise<DriverInvitation> =>
  (await axiosInstance.post<DriverInvitation>(`${BASE}/drivers`, input)).data;
export const updateDriver = async (id: string, input: DriverChangeInput): Promise<Driver> =>
  (await axiosInstance.put<{ data: Driver }>(`${BASE}/drivers/${id}`, input)).data.data;
export const setDriverActive = async (id: string, active: boolean): Promise<Driver> =>
  (await axiosInstance.post<{ data: Driver }>(`${BASE}/drivers/${id}/active`, { active })).data.data;
export const resendInvitation = async (id: string, language: string): Promise<DriverInvitation> =>
  (await axiosInstance.post<DriverInvitation>(`${BASE}/drivers/${id}/invitation`, { language })).data;

export const getLoads = async (query: LoadQuery): Promise<Load[]> =>
  (await axiosInstance.get<{ data: Load[] }>(`${BASE}/loads`, { params: query })).data.data;
/** One load by id, wherever it is in time (an AI answer's link) */
export const getLoad = async (id: string): Promise<Load> => (await axiosInstance.get<{ data: Load }>(`${BASE}/loads/${id}`)).data.data;
export const createLoad = async (input: LoadInput): Promise<Load> =>
  (await axiosInstance.post<{ data: Load }>(`${BASE}/loads`, input)).data.data;
export const updateLoad = async (id: string, input: Partial<LoadInput>): Promise<Load> =>
  (await axiosInstance.put<{ data: Load }>(`${BASE}/loads/${id}`, input)).data.data;
export const setLoadStatus = async (id: string, status: LoadStatus): Promise<Load> =>
  (await axiosInstance.post<{ data: Load }>(`${BASE}/loads/${id}/status`, { status })).data.data;

export const getLive = async (): Promise<LiveBoard> => (await axiosInstance.get<{ data: LiveBoard }>(`${BASE}/live`)).data.data;
export const getTracking = async (id: string): Promise<TuraTracking> =>
  (await axiosInstance.get<{ data: TuraTracking }>(`${BASE}/loads/${id}/tracking`)).data.data;
export const setTracking = async (id: string, enabled: boolean): Promise<Load> =>
  (await axiosInstance.put<{ data: Load }>(`${BASE}/loads/${id}/tracking`, { enabled })).data.data;

export const getMyLoads = async (): Promise<MyLoads> =>
  (await axiosInstance.get<{ data: MyLoads }>(`${BASE}/my/loads`)).data.data;
export const markPickedUp = async (id: string): Promise<Load> =>
  (await axiosInstance.post<{ data: Load }>(`${BASE}/my/loads/${id}/picked-up`)).data.data;
export const markDelivered = async (id: string): Promise<Load> =>
  (await axiosInstance.post<{ data: Load }>(`${BASE}/my/loads/${id}/delivered`)).data.data;

export type RoutePreference = 'fastest' | 'shortest' | 'no_tolls';

/** One route option: km, truck drive time, each leg's drive, and the line to draw */
export interface RouteOption {
  preference: RoutePreference;
  distanceM: number;
  driveS: number;
  legsS: number[] | null;
  /** ORS's encoded polyline (precision 5) */
  geometry: string | null;
  /** A LocationIQ car route, its times ×1.1 for a truck, standing in while ORS cannot answer */
  estimate?: boolean;
}

/** What ORS answers for the dialog's stops before anything is saved: both options when it finds a route */
export interface RoutePreview {
  status: 'ok' | 'not_found' | 'unavailable' | 'off';
  options: RouteOption[];
}

/** A suggestion, or a point named on the map; `detail` is the full address that tells like names apart */
export interface PlaceHit {
  label: string;
  detail?: string | null;
  lat: number;
  lon: number;
}

/** Place suggestions and who gave them: LocationIQ asks for its credit under the list */
export interface PlaceSearch {
  status: 'ok' | 'off' | 'unavailable';
  source: 'locationiq' | 'ors' | null;
  places: PlaceHit[];
}

export const searchPlaces = async (text: string): Promise<PlaceSearch> =>
  (await axiosInstance.get<{ data: PlaceSearch }>(`${BASE}/places`, { params: { text } })).data.data;

/** The map background from echo: LocationIQ's tiles (they carry its public key), or null for CARTO */
export interface MapTiles {
  light: string;
  dark: string;
  attribution: string;
}

export const fetchMapTiles = async (): Promise<MapTiles | null> =>
  (await axiosInstance.get<{ data: { tiles: MapTiles | null } }>(`${BASE}/map`)).data.data.tiles;

/** The planner's colours: a colour per driver row, or every row in blues, greys, warm neutrals or earth tones */
export type PlannerTheme = 'colors' | 'slate' | 'graphite' | 'neutral' | 'earth';
export const PLANNER_THEMES: PlannerTheme[] = ['colors', 'slate', 'graphite', 'neutral', 'earth'];

/** The signed-in user's own settings, the same on every device */
export interface MySettings {
  plannerTheme: PlannerTheme;
}

export const getMySettings = async (): Promise<MySettings> => (await axiosInstance.get<{ data: MySettings }>(`${BASE}/me/settings`)).data.data;
export const saveMySettings = async (settings: MySettings): Promise<MySettings> =>
  (await axiosInstance.put<{ data: MySettings }>(`${BASE}/me/settings`, settings)).data.data;

export const previewRoute = async (stops: Array<{ place: string; lat?: number; lon?: number }>, options: RoutePreference[]): Promise<RoutePreview> =>
  (await axiosInstance.post<{ data: RoutePreview }>(`${BASE}/routes/preview`, { stops, options })).data.data;

export const reversePlace = async (lat: number, lon: number): Promise<{ status: string; label: string | null }> =>
  (await axiosInstance.get<{ data: { status: string; label: string | null } }>(`${BASE}/places/reverse`, { params: { lat, lon } })).data.data;
